import React, { createContext, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { FeatureFlagKey, KameleoonFeatureFlagVariables } from '@reward-platform/featureFlags'
import { getActiveFeatureFlags } from '~/services/featureFlagService'
import { PlatformError } from '~/utils/errors'
import { useAuthContext } from '../auth'
import { getAuthUserSub, getAuthUserEmail } from '../auth/Auth.types'
import { evaluateFeatureFlagValue } from './FeatureFlag.utils'
import type { FeatureFlagContextValue, FeatureFlagProviderProps } from './FeatureFlag.types'

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined)

export const FeatureFlagProvider = ({
  children,
  initialFeatureFlags,
}: React.PropsWithChildren<FeatureFlagProviderProps>) => {
  const { user } = useAuthContext()
  const { data, isLoading, isError } = useQuery({
    queryKey: user ? [getAuthUserSub(user), getAuthUserEmail(user)] : ['unauthenticated_user'],
    queryFn: getActiveFeatureFlags,
    initialData: initialFeatureFlags,
  })
  const activeFeatureFlags = useMemo(() => (data && !isError ? { ...data } : {}), [data, isError])

  const context: FeatureFlagContextValue = useMemo(
    () => ({ activeFeatureFlags, isLoading }),
    [activeFeatureFlags, isLoading]
  )

  return <FeatureFlagContext.Provider value={context}>{children}</FeatureFlagContext.Provider>
}

export const useFeatureFlagContext = (): FeatureFlagContextValue => {
  const context = useContext(FeatureFlagContext)
  if (!context) {
    throw new PlatformError('useFeatureFlagContext must be used within an FeatureFlagProvider')
  }
  return context ?? {}
}

export const useFeatureFlag = (key: FeatureFlagKey, defaultValue = false): boolean => {
  const { activeFeatureFlags } = useFeatureFlagContext()

  return !!evaluateFeatureFlagValue<boolean>(activeFeatureFlags, key, defaultValue)
}

export const useFeatureFlagWithMultipleVariables = <K extends keyof KameleoonFeatureFlagVariables>(
  key: K,
  defaultValue = false
): { value: boolean; variables: Partial<KameleoonFeatureFlagVariables[K]> } => {
  const { activeFeatureFlags } = useFeatureFlagContext()
  const feature = activeFeatureFlags[key]

  return {
    value: !!evaluateFeatureFlagValue<boolean>(activeFeatureFlags, key, defaultValue),
    variables: (feature?.variables ?? {}) as Partial<KameleoonFeatureFlagVariables[K]>,
  }
}

export function useFeatureFlagVariation(key: FeatureFlagKey): unknown {
  const { activeFeatureFlags } = useFeatureFlagContext()
  return evaluateFeatureFlagValue<string>(activeFeatureFlags, key)
}

export default FeatureFlagContext
